// Pipelines view — system flow chart with live counts. Each pipeline
// renders as a horizontal sequence of stage cards connected by arrows.
// Cards show count + last/next time + click to detail page.
//
// Color coding:
//   green   = healthy (low queue depth or recent activity)
//   amber   = building (queue growing) or idle agent past due
//   red     = stuck (high queue depth + no recent agent activity)
//   gray    = idle / not yet active
//
// Auto-refreshes every 15s. Cards are clickable; the click goes to
// the linkTo path (existing dashboard pages — Inbox / Site Detail /
// Optimize / Agents / etc.). Hover surfaces the stage description.

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { pipelinesApi, type PipelineLive, type PipelineStageLive } from "../lib/api";

export function PipelinesPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["pipelines"],
    queryFn: () => pipelinesApi.list(),
    refetchInterval: 15_000,
  });

  if (isLoading) return <div className="text-sm text-ink-500">Loading flows…</div>;
  if (error) return <div className="text-sm text-red-700">Failed: {String((error as Error).message)}</div>;
  if (!data) return null;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-display font-bold text-ink-900">System flows</h1>
        <p className="text-sm text-ink-600 mt-1">
          Live view of the {data.pipelines.length} pipelines that move data through RankForge.
          Counts refresh every 15s. Click any stage for details.
        </p>
      </div>

      <div className="space-y-8">
        {data.pipelines.map((p) => (
          <PipelineRow key={p.id} pipeline={p} />
        ))}
      </div>

      <div className="mt-10 text-xs text-ink-500 border-t border-ink-100 pt-4">
        <strong>Reading the colors.</strong>{" "}
        <span className="inline-block w-3 h-3 rounded-sm bg-emerald-500 align-middle mr-1" />green = healthy / draining{" "}
        <span className="inline-block w-3 h-3 rounded-sm bg-amber-500 align-middle ml-3 mr-1" />amber = building or due{" "}
        <span className="inline-block w-3 h-3 rounded-sm bg-red-500 align-middle ml-3 mr-1" />red = stuck (high count + idle){" "}
        <span className="inline-block w-3 h-3 rounded-sm bg-ink-300 align-middle ml-3 mr-1" />gray = idle (no traffic){" "}
      </div>
    </div>
  );
}

function PipelineRow({ pipeline }: { pipeline: PipelineLive }) {
  return (
    <section className="bg-white rounded-xl border border-ink-100 shadow-sm p-5">
      <header className="mb-4">
        <h2 className="font-semibold text-ink-900">{pipeline.name}</h2>
        <p className="text-xs text-ink-600 mt-1">{pipeline.description}</p>
      </header>
      <div className="flex items-stretch gap-2 overflow-x-auto pb-2">
        {pipeline.stages.map((s, i) => (
          <div key={s.id} className="flex items-stretch gap-2 shrink-0">
            <StageCard stage={s} />
            {i < pipeline.stages.length - 1 && <Arrow />}
          </div>
        ))}
      </div>
    </section>
  );
}

function StageCard({ stage }: { stage: PipelineStageLive }) {
  const tone = computeTone(stage);
  const palette = TONE_PALETTE[tone];

  const inner = (
    <div
      className={`w-44 h-full rounded-lg border ${palette.border} ${palette.bg} px-3 py-2.5 flex flex-col gap-1.5 text-left transition-shadow ${stage.linkTo ? "hover:shadow-md cursor-pointer" : ""}`}
      title={stage.description ?? undefined}
    >
      <div className="flex items-center gap-1.5">
        <KindIcon kind={stage.kind} />
        <span className={`text-[10px] font-mono uppercase tracking-wider ${palette.kindText}`}>
          {stage.kind}
        </span>
      </div>
      <div className="font-medium text-sm text-ink-900 leading-tight">{stage.label}</div>
      <CardBody stage={stage} tone={tone} />
    </div>
  );

  if (stage.linkTo) {
    return <Link to={stage.linkTo} className="no-underline">{inner}</Link>;
  }
  return inner;
}

function CardBody({ stage, tone }: { stage: PipelineStageLive; tone: Tone }) {
  if (stage.kind === "agent") {
    return (
      <div className="mt-auto space-y-0.5">
        <div className="flex items-baseline justify-between">
          <span className="text-[10px] text-ink-500">last</span>
          <span className="text-xs tabular-nums text-ink-700">
            {stage.lastRunAt ? relTime(stage.lastRunAt) : "—"}
          </span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="text-[10px] text-ink-500">next</span>
          <span className={`text-xs tabular-nums ${
            tone === "amber" ? "text-amber-700 font-medium" : "text-ink-700"
          }`}>
            {stage.nextRunAt ? relTimeFuture(stage.nextRunAt) : "—"}
          </span>
        </div>
        {stage.agentTrustPhase && (
          <div className="text-[10px] text-ink-500 mt-0.5">
            {stage.agentEnabled === false ? <span className="text-ink-400">disabled</span> : stage.agentTrustPhase}
          </div>
        )}
      </div>
    );
  }
  if (stage.kind === "manual") {
    return (
      <div className="mt-auto text-[10px] text-ink-500">
        operator action
      </div>
    );
  }
  // queue / alerts / view — render the count
  if (stage.count == null) {
    return <div className="mt-auto text-xs text-ink-400">—</div>;
  }
  return (
    <div className="mt-auto">
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-bold text-ink-900 tabular-nums leading-none">
          {fmtCount(stage.count)}
        </span>
        {stage.countLabel && (
          <span className="text-[10px] text-ink-500">{stage.countLabel}</span>
        )}
      </div>
    </div>
  );
}

function Arrow() {
  return (
    <div className="self-center text-ink-400 select-none px-1">
      <svg width="20" height="14" viewBox="0 0 20 14" fill="none">
        <path
          d="M0 7h17m0 0l-5-5m5 5l-5 5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function KindIcon({ kind }: { kind: PipelineStageLive["kind"] }) {
  // Tiny inline SVG icons keep the bundle small + the icons don't depend
  // on a font.
  const fill = "currentColor";
  if (kind === "agent") {
    return (
      <svg width="11" height="11" viewBox="0 0 16 16" fill={fill}><circle cx="8" cy="6" r="3" /><path d="M2 14a6 6 0 0112 0z" /></svg>
    );
  }
  if (kind === "queue") {
    return <svg width="11" height="11" viewBox="0 0 16 16" fill={fill}><rect x="2" y="3" width="12" height="2" /><rect x="2" y="7" width="12" height="2" /><rect x="2" y="11" width="12" height="2" /></svg>;
  }
  if (kind === "alerts") {
    return <svg width="11" height="11" viewBox="0 0 16 16" fill={fill}><path d="M8 1L1 14h14z" /></svg>;
  }
  if (kind === "manual") {
    return <svg width="11" height="11" viewBox="0 0 16 16" fill={fill}><circle cx="8" cy="8" r="3" /><circle cx="8" cy="8" r="6" stroke={fill} fill="none" strokeWidth="1.5" /></svg>;
  }
  // view
  return <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="3" stroke={fill} strokeWidth="1.5" /><circle cx="8" cy="8" r="6.5" stroke={fill} strokeWidth="1.5" /></svg>;
}

// ─── tone / color logic ─────────────────────────────────────────────

type Tone = "green" | "amber" | "red" | "gray";

const TONE_PALETTE: Record<Tone, {
  bg: string;
  border: string;
  kindText: string;
}> = {
  green: { bg: "bg-emerald-50", border: "border-emerald-200", kindText: "text-emerald-700" },
  amber: { bg: "bg-amber-50",   border: "border-amber-200",   kindText: "text-amber-700" },
  red:   { bg: "bg-red-50",     border: "border-red-200",     kindText: "text-red-700" },
  gray:  { bg: "bg-ink-50",     border: "border-ink-200",     kindText: "text-ink-500" },
};

function computeTone(s: PipelineStageLive): Tone {
  if (s.kind === "agent") {
    if (s.agentEnabled === false) return "gray";
    if (!s.lastRunAt) return "gray";
    const lastMs = Date.now() - new Date(s.lastRunAt).getTime();
    const interval = (s.intervalSec ?? 300) * 1000;
    // Past due by > 2 intervals → amber. > 4 intervals + recent action → red.
    if (s.nextRunAt) {
      const dueIn = new Date(s.nextRunAt).getTime() - Date.now();
      if (dueIn > -interval) return "green"; // on track or just past due
      if (lastMs > 4 * interval) return "red"; // very overdue
      return "amber";
    }
    if (lastMs > 4 * interval) return "red";
    return "green";
  }
  if (s.kind === "alerts") {
    if (s.count == null) return "gray";
    if (s.count === 0) return "green";
    if (s.count >= 20) return "red";
    return "amber";
  }
  if (s.kind === "queue") {
    if (s.count == null) return "gray";
    if (s.count === 0) return "gray";
    // Heuristic threshold: queues holding > 1000 rows for "pending"
    // states are probably stuck. Tuned to RankForge defaults; can be
    // made per-stage later.
    if (s.id.includes("pending") && s.count > 1500) return "red";
    if (s.id.includes("pending") && s.count > 500) return "amber";
    return "green";
  }
  if (s.kind === "view") {
    return s.count != null && s.count > 0 ? "green" : "gray";
  }
  // manual
  return "gray";
}

// ─── formatters ─────────────────────────────────────────────────────

function fmtCount(n: number): string {
  if (n >= 10_000) return `${(n / 1000).toFixed(0)}k`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  return `${d}d ago`;
}

function relTimeFuture(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "due now";
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `in ${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `in ${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `in ${hr}h`;
  const d = Math.round(hr / 24);
  return `in ${d}d`;
}
